import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import empleadoConceptoService from '../services/empleadoConceptoService';
import {
  CODIGO_SUELDO_BASE,
  armarCambiosConRecalculo,
  cargarDependenciasCalculoMasivo,
  ingresosPlameDeEmpleado,
  normalizarMonto,
} from '../utils/actualizacionMasivaIngresosCalculo';
import {
  aplicarFilasExcelACeldas,
  escribirWorkbookIngresosMasivo,
  etiquetaAfpPension,
  etiquetaRegimenLaboral,
  leerFilasIngresosDesdeExcel,
} from '../utils/actualizacionMasivaIngresosExcel';

const ActualizacionMasivaIngresosModal = ({
  isOpen,
  onClose,
  empresaId,
  empresa,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progresoGuardado, setProgresoGuardado] = useState('');
  const [exportando, setExportando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [infoImport, setInfoImport] = useState(null);
  const [mostrarCatalogo, setMostrarCatalogo] = useState(false);
  const inputExcelRef = useRef(null);
  const [empleados, setEmpleados] = useState([]);
  const [depsCalculo, setDepsCalculo] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [soloActivos, setSoloActivos] = useState(true);
  /** { [conceptoId]: { monto, original, empleadoId, codigo } } */
  const [celdas, setCeldas] = useState({});
  const [sueldosBasico, setSueldosBasico] = useState({});
  const [error, setError] = useState(null);

  const cargarDatos = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError(null);
    try {
      const [resultado, deps] = await Promise.all([
        empleadoConceptoService.obtenerEmpleadosConConceptosPorEmpresa(empresaId),
        cargarDependenciasCalculoMasivo(empresaId),
      ]);
      const lista = resultado?.data || [];
      setEmpleados(lista);
      setDepsCalculo(deps);

      const nextCeldas = {};
      const nextSueldos = {};
      lista.forEach((emp) => {
        nextSueldos[emp.id] = normalizarMonto(emp.sueldo_basico);
        ingresosPlameDeEmpleado(emp).forEach((c) => {
          const monto = normalizarMonto(c.monto);
          nextCeldas[c.id] = {
            monto,
            original: monto,
            empleadoId: emp.id,
            codigo: c.plame_configuracion?.codigo_plame || '',
            descripcion: c.plame_configuracion?.descripcion || '',
          };
        });
      });
      setCeldas(nextCeldas);
      setSueldosBasico(nextSueldos);
    } catch (e) {
      setError(e.message || 'No se pudo cargar el personal');
      setEmpleados([]);
      setCeldas({});
      setDepsCalculo(null);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    if (isOpen && empresaId) {
      cargarDatos();
    }
  }, [isOpen, empresaId, cargarDatos]);

  const columnasIngreso = useMemo(() => {
    const map = new Map();
    empleados.forEach((emp) => {
      ingresosPlameDeEmpleado(emp).forEach((c) => {
        const codigo = c.plame_configuracion?.codigo_plame || '0000';
        if (!map.has(codigo)) {
          map.set(codigo, {
            codigo,
            descripcion: c.plame_configuracion?.descripcion || '',
          });
        }
      });
    });
    return Array.from(map.values())
      .filter((col) => col.codigo !== CODIGO_SUELDO_BASE)
      .sort((a, b) =>
        a.codigo.localeCompare(b.codigo, undefined, { numeric: true })
      );
  }, [empleados]);

  const descripcion0121 = useMemo(() => {
    for (const emp of empleados) {
      const c = ingresosPlameDeEmpleado(emp).find(
        (x) => x.plame_configuracion?.codigo_plame === CODIGO_SUELDO_BASE
      );
      if (c?.plame_configuracion?.descripcion) {
        return c.plame_configuracion.descripcion;
      }
    }
    return 'Remuneración o jornal básico';
  }, [empleados]);

  const catalogoPlame = useMemo(() => {
    const items = [
      {
        codigo: CODIGO_SUELDO_BASE,
        descripcion: descripcion0121,
        nota: 'Actualiza sueldo básico',
      },
      ...columnasIngreso.map((c) => ({
        codigo: c.codigo,
        descripcion: c.descripcion,
        nota: c.codigo === '0902' ? 'Actualiza Tiene bono / monto bono' : '',
      })),
    ];
    return items.sort((a, b) =>
      a.codigo.localeCompare(b.codigo, undefined, { numeric: true })
    );
  }, [columnasIngreso, descripcion0121]);

  const filas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return empleados
      .filter((emp) => {
        if (soloActivos && String(emp.estado) !== 'Activo') return false;
        if (!q) return true;
        const nombre = String(emp.nombre_completo || '').toLowerCase();
        const doc = String(emp.numero_documento || '').toLowerCase();
        const cargo = String(emp.cargo || '').toLowerCase();
        const proyecto = String(emp.proyecto || '').toLowerCase();
        return (
          nombre.includes(q) ||
          doc.includes(q) ||
          cargo.includes(q) ||
          proyecto.includes(q)
        );
      })
      .sort((a, b) =>
        String(a.nombre_completo || '').localeCompare(String(b.nombre_completo || ''), 'es')
      );
  }, [empleados, busqueda, soloActivos]);

  const cambiosPendientes = useMemo(
    () =>
      Object.entries(celdas)
        .filter(([, cell]) => cell.monto !== cell.original)
        .map(([conceptoId, cell]) => ({
          empleado_id: cell.empleadoId,
          concepto_id: Number(conceptoId),
          monto: cell.monto,
        })),
    [celdas]
  );

  const handleMontoChange = (conceptoId, valorRaw) => {
    const monto = normalizarMonto(valorRaw);
    setCeldas((prev) => {
      const cell = prev[conceptoId];
      if (!cell) return prev;
      if (cell.codigo === CODIGO_SUELDO_BASE) {
        setSueldosBasico((p) => ({ ...p, [cell.empleadoId]: monto }));
      }
      return { ...prev, [conceptoId]: { ...cell, monto } };
    });
  };

  const concepto0121DeEmpleado = (emp) =>
    ingresosPlameDeEmpleado(emp).find(
      (c) => c.plame_configuracion?.codigo_plame === CODIGO_SUELDO_BASE
    );

  const montoIngresoCelda = (emp, codigo) => {
    const concepto = ingresosPlameDeEmpleado(emp).find(
      (c) => c.plame_configuracion?.codigo_plame === codigo
    );
    if (!concepto) return null;
    return celdas[concepto.id]?.monto ?? normalizarMonto(concepto.monto);
  };

  const resetearCambios = () => {
    setCeldas((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        next[id] = { ...next[id], monto: next[id].original };
      });
      return next;
    });
    setSueldosBasico(() => {
      const next = {};
      empleados.forEach((emp) => {
        next[emp.id] = normalizarMonto(emp.sueldo_basico);
      });
      return next;
    });
  };

  const guardar = async () => {
    if (!cambiosPendientes.length) {
      onClose?.();
      return;
    }
    if (!depsCalculo) {
      setError('Faltan datos de cálculo. Cierre y vuelva a abrir el modal.');
      return;
    }

    setSaving(true);
    setError(null);
    setProgresoGuardado('Recalculando descuentos y aportes…');

    try {
      const { cambios, advertencias } = await armarCambiosConRecalculo(
        empleados,
        celdas,
        depsCalculo,
        empresa || { id: empresaId }
      );

      if (!cambios.length) {
        throw new Error('No hay cambios válidos para guardar');
      }

      setProgresoGuardado(`Guardando ${cambios.length} concepto(s)…`);

      const resp = await empleadoConceptoService.actualizarIngresosMasivo(
        empresaId,
        cambios
      );
      if (!resp.success) {
        throw new Error(resp.message || 'Error al guardar');
      }

      let msg =
        resp.message ||
        `Se actualizaron ${resp.data?.conceptos_actualizados ?? cambios.length} concepto(s)`;
      if (advertencias.length) {
        msg += `. Avisos: ${advertencias.slice(0, 3).join('; ')}`;
        if (advertencias.length > 3) msg += ` (+${advertencias.length - 3} más)`;
      }
      onSuccess?.(msg, resp.data);
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Error al guardar');
    } finally {
      setSaving(false);
      setProgresoGuardado('');
    }
  };

  const exportarExcel = async () => {
    setExportando(true);
    setError(null);
    try {
      const nombreEmpresa =
        empresa?.name || empresa?.nombre_empresa || empresa?.nombre || 'Empresa';

      const filasData = filas.map((emp) => [
        emp.numero_documento || '',
        emp.nombre_completo || '',
        emp.cargo || '',
        emp.proyecto || '',
        emp.categoria || '',
        etiquetaRegimenLaboral(emp),
        etiquetaAfpPension(emp),
        emp.estado || '',
        montoIngresoCelda(emp, CODIGO_SUELDO_BASE) ?? '',
        ...columnasIngreso.map((col) => montoIngresoCelda(emp, col.codigo) ?? ''),
      ]);

      const { buffer } = await escribirWorkbookIngresosMasivo({
        filasEmpleado: filasData,
        columnasIngreso,
        descripcion0121,
        nombreEmpresa,
      });

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fecha = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `ingresos_masivo_${nombreEmpresa.replace(/\s+/g, '_')}_${fecha}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'No se pudo exportar a Excel');
    } finally {
      setExportando(false);
    }
  };

  const importarExcel = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportando(true);
    setError(null);
    setInfoImport(null);
    try {
      const { filas: filasExcel, columnasDetectadas } =
        await leerFilasIngresosDesdeExcel(file);

      const resultado = aplicarFilasExcelACeldas(
        empleados,
        celdas,
        filasExcel,
        ingresosPlameDeEmpleado
      );

      setCeldas(resultado.celdas);
      setSueldosBasico((prev) => ({ ...prev, ...resultado.sueldosBasico }));

      let msg = `Importado: ${resultado.celdasActualizadas} monto(s) en ${resultado.filasOk} trabajador(es). Códigos: ${columnasDetectadas.join(', ')}.`;
      if (resultado.noEncontrados.length) {
        msg += ` Documentos no encontrados: ${resultado.noEncontrados.slice(0, 5).join(', ')}`;
        if (resultado.noEncontrados.length > 5) {
          msg += ` (+${resultado.noEncontrados.length - 5})`;
        }
      }
      if (resultado.sinConcepto.length) {
        msg += ` Sin concepto en ficha: ${resultado.sinConcepto.slice(0, 3).join('; ')}`;
      }
      msg += ' Revise y pulse Guardar y recalcular.';
      setInfoImport(msg);
    } catch (e) {
      setError(e.message || 'No se pudo importar el Excel');
    } finally {
      setImportando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="inline-block w-full max-w-[98vw] transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle">
          <div className="bg-green-700 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Actualización masiva — conceptos de ingreso
              </h2>
              <p className="text-green-100 text-sm mt-1">
                Al guardar se recalculan descuentos y aportes con fórmula (ONP, AFP, EsSalud, renta).
                El {CODIGO_SUELDO_BASE} actualiza el sueldo básico; el 0902 actualiza «Tiene bono».
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-green-100 hover:text-white text-2xl leading-none"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>

          <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap gap-4 items-center">
            <input
              type="search"
              placeholder="Buscar por nombre, documento, cargo o proyecto…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="flex-1 min-w-[200px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
            />
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={soloActivos}
                onChange={(e) => setSoloActivos(e.target.checked)}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              Solo activos
            </label>
            <button
              type="button"
              onClick={() => setMostrarCatalogo((v) => !v)}
              className="text-sm text-green-800 underline hover:text-green-900"
            >
              {mostrarCatalogo ? 'Ocultar' : 'Ver'} conceptos PLAME
            </button>
            <input
              ref={inputExcelRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={importarExcel}
            />
            <button
              type="button"
              onClick={() => inputExcelRef.current?.click()}
              disabled={loading || importando}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-teal-800 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100 disabled:opacity-50"
            >
              {importando ? 'Importando…' : 'Importar Excel'}
            </button>
            <button
              type="button"
              onClick={exportarExcel}
              disabled={loading || exportando || filas.length === 0}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 disabled:opacity-50"
            >
              {exportando ? 'Exportando…' : 'Exportar Excel'}
            </button>
            {cambiosPendientes.length > 0 && (
              <span className="text-sm font-medium text-amber-700">
                {cambiosPendientes.length} cambio(s) pendiente(s)
              </span>
            )}
          </div>

          {infoImport && (
            <div className="mx-6 mt-4 p-3 rounded-md bg-blue-50 text-blue-800 text-sm border border-blue-200">
              {infoImport}
            </div>
          )}

          {mostrarCatalogo && catalogoPlame.length > 0 && (
            <div className="mx-6 mt-4 p-3 rounded-md bg-gray-50 border border-gray-200 text-sm overflow-x-auto">
              <p className="font-medium text-gray-800 mb-2">
                Conceptos PLAME (ingresos) en esta empresa
              </p>
              <table className="min-w-full text-left">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase">
                    <th className="pr-4 pb-1">Código</th>
                    <th className="pr-4 pb-1">Descripción</th>
                    <th className="pb-1">Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogoPlame.map((item) => (
                    <tr key={item.codigo} className="border-t border-gray-200">
                      <td className="py-1 pr-4 font-mono font-semibold">{item.codigo}</td>
                      <td className="py-1 pr-4 text-gray-700">{item.descripcion || '—'}</td>
                      <td className="py-1 text-gray-500 text-xs">{item.nota}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {error && (
            <div className="mx-6 mt-4 p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">
              {error}
            </div>
          )}

          <div className="px-6 py-4 max-h-[60vh] overflow-auto">
            {loading ? (
              <div className="py-16 text-center text-gray-500">Cargando personal…</div>
            ) : filas.length === 0 ? (
              <div className="py-16 text-center text-gray-500">
                No hay trabajadores que coincidan con el filtro.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700 min-w-[200px]">
                      Empleado
                    </th>
                    <th className="px-2 py-3 text-left font-semibold text-gray-600 min-w-[120px]">
                      Cargo
                    </th>
                    <th className="px-2 py-3 text-left font-semibold text-gray-600 min-w-[100px]">
                      Proyecto
                    </th>
                    <th className="px-2 py-3 text-left font-semibold text-gray-600 min-w-[90px]">
                      Categoría
                    </th>
                    <th className="px-2 py-3 text-left font-semibold text-gray-600 min-w-[130px]">
                      Régimen laboral
                    </th>
                    <th className="px-2 py-3 text-left font-semibold text-gray-600 min-w-[120px]">
                      AFP / ONP
                    </th>
                    <th className="px-3 py-3 text-left font-semibold text-green-900 min-w-[110px] bg-green-50">
                      <span className="font-mono">{CODIGO_SUELDO_BASE}</span>
                      <span className="block text-xs font-normal text-green-700">Sueldo básico</span>
                      <span className="block text-[10px] font-normal text-green-600 line-clamp-2">
                        {descripcion0121}
                      </span>
                    </th>
                    {columnasIngreso.map((col) => (
                      <th
                        key={col.codigo}
                        className="px-2 py-3 text-center font-semibold min-w-[90px] text-gray-700"
                        title={col.descripcion}
                      >
                        <span className="font-mono">{col.codigo}</span>
                        {col.descripcion ? (
                          <span className="block text-[10px] font-normal text-gray-500 line-clamp-2">
                            {col.descripcion}
                          </span>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filas.map((emp) => {
                    const ingresos = ingresosPlameDeEmpleado(emp);
                    const c0121 = concepto0121DeEmpleado(emp);
                    const cell0121 = c0121 ? celdas[c0121.id] : null;
                    const sueldoMostrar =
                      cell0121?.monto ??
                      sueldosBasico[emp.id] ??
                      normalizarMonto(emp.sueldo_basico);
                    const modificado0121 = cell0121 && cell0121.monto !== cell0121.original;
                    return (
                      <tr key={emp.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 align-top">
                          <div className="font-medium text-gray-900">{emp.nombre_completo}</div>
                          <div className="text-xs text-gray-500">{emp.numero_documento}</div>
                          <div className="text-xs text-gray-400">{emp.estado}</div>
                        </td>
                        <td className="px-2 py-2 align-top text-gray-700 text-xs">
                          {emp.cargo || '—'}
                        </td>
                        <td className="px-2 py-2 align-top text-gray-700 text-xs">
                          {emp.proyecto || '—'}
                        </td>
                        <td className="px-2 py-2 align-top text-gray-700 text-xs">
                          {emp.categoria || '—'}
                        </td>
                        <td className="px-2 py-2 align-top text-gray-700 text-xs">
                          {etiquetaRegimenLaboral(emp)}
                        </td>
                        <td className="px-2 py-2 align-top text-gray-700 text-xs">
                          {etiquetaAfpPension(emp)}
                        </td>
                        <td className="px-3 py-2 align-top bg-green-50/50">
                          {c0121 ? (
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                                S/
                              </span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={sueldoMostrar}
                                onChange={(e) =>
                                  handleMontoChange(c0121.id, e.target.value)
                                }
                                className={`w-full max-w-[110px] pl-7 pr-1 py-1.5 border rounded text-sm tabular-nums ${
                                  modificado0121
                                    ? 'border-amber-400 bg-amber-50'
                                    : 'border-green-300 bg-white'
                                } focus:ring-green-500 focus:border-green-500`}
                              />
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">Sin {CODIGO_SUELDO_BASE}</span>
                          )}
                        </td>
                        {columnasIngreso.map((col) => {
                          const concepto = ingresos.find(
                            (c) => c.plame_configuracion?.codigo_plame === col.codigo
                          );
                          if (!concepto) {
                            return (
                              <td key={col.codigo} className="px-2 py-2 text-center text-gray-300">
                                —
                              </td>
                            );
                          }
                          const cell = celdas[concepto.id];
                          const modificado = cell && cell.monto !== cell.original;
                          return (
                            <td key={col.codigo} className="px-2 py-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={cell?.monto ?? normalizarMonto(concepto.monto)}
                                onChange={(e) =>
                                  handleMontoChange(concepto.id, e.target.value)
                                }
                                className={`w-full max-w-[100px] mx-auto block pl-2 pr-1 py-1.5 border rounded text-sm tabular-nums ${
                                  modificado
                                    ? 'border-amber-400 bg-amber-50'
                                    : 'border-gray-300 bg-white'
                                } focus:ring-green-500 focus:border-green-500`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-gray-50 px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-t">
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={resetearCambios}
                disabled={!cambiosPendientes.length || saving}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 text-left"
              >
                Deshacer cambios
              </button>
              {progresoGuardado && (
                <span className="text-xs text-gray-500">{progresoGuardado}</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardar}
                disabled={saving || !cambiosPendientes.length}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving
                  ? 'Guardando…'
                  : `Guardar y recalcular (${cambiosPendientes.length})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActualizacionMasivaIngresosModal;
